import { useCallback, useEffect, useState } from "react";
import {
  Button,
  DatePicker,
  Flex,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import { type Dayjs } from "dayjs";
import {
  EyeOutlined,
  ReloadOutlined,
  SendOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { api, ReportConfig, ReportHistory } from "../api";

const { Text } = Typography;
const { RangePicker } = DatePicker;

export default function ReportHistoryPage() {
  const [history, setHistory]   = useState<ReportHistory[]>([]);
  const [configs, setConfigs]   = useState<ReportConfig[]>([]);
  const [loading, setLoading]   = useState(false);
  const [filterCfg, setFilterCfg] = useState<number | undefined>(undefined);
  const [preview, setPreview]   = useState<ReportHistory | null>(null);
  const [sending, setSending]   = useState<number | null>(null);

  // manual-send modal
  const [sendModal, setSendModal] = useState(false);
  const [sendCfgID, setSendCfgID] = useState<number | undefined>(undefined);
  const [sendRange, setSendRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [sendLoading, setSendLoading] = useState(false);

  const load = useCallback(async (cfgId?: number) => {
    setLoading(true);
    try {
      const [hist, cfgs] = await Promise.all([
        api.reportHistory({ config_id: cfgId, limit: 100 }),
        api.reportConfigs(),
      ]);
      setHistory(hist);
      setConfigs(cfgs);
    } catch (e: any) {
      message.error(e.message ?? "Load failed");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(filterCfg); }, [load, filterCfg]);

  async function handleResend(h: ReportHistory) {
    setSending(h.id);
    const sentAt = new Date();
    try {
      await api.resendReport(h.id);
      message.loading({ content: "Re-generating…", key: `resend-${h.id}`, duration: 0 });
      for (let i = 0; i < 36; i++) {
        await new Promise(r => setTimeout(r, 5000));
        try {
          const hist = await api.reportHistory({ config_id: h.config_id, limit: 1 });
          if (hist.length && new Date(hist[0].sent_at) >= sentAt) {
            message.destroy(`resend-${h.id}`);
            message.success("Report re-sent");
            setPreview(hist[0]);
            load(filterCfg);
            return;
          }
        } catch (_) { /* ignore */ }
      }
      message.destroy(`resend-${h.id}`);
      message.info("Generating in background — refresh History");
      load(filterCfg);
    } catch (e: any) {
      message.error(e.message ?? "Resend failed");
    } finally {
      setSending(null);
    }
  }

  async function handleManualSend() {
    if (!sendCfgID) { message.warning("Select a config"); return; }
    setSendLoading(true);
    const sentAt = new Date();
    try {
      const opts = sendRange
        ? { start_unix: sendRange[0].unix(), end_unix: sendRange[1].unix() }
        : undefined;
      await api.sendReport(sendCfgID, opts);
      setSendModal(false);
      setSendRange(null);
      message.loading({ content: "Generating report…", key: "manual-send", duration: 0 });
      for (let i = 0; i < 36; i++) {
        await new Promise(r => setTimeout(r, 5000));
        try {
          const hist = await api.reportHistory({ config_id: sendCfgID, limit: 1 });
          if (hist.length && new Date(hist[0].sent_at) >= sentAt) {
            message.destroy("manual-send");
            if (hist[0].status === "sent") {
              message.success("Report sent");
            } else {
              message.error(`Failed: ${hist[0].error || "unknown"}`);
            }
            setPreview(hist[0]);
            load(filterCfg);
            return;
          }
        } catch (_) { /* ignore */ }
      }
      message.destroy("manual-send");
      message.info("Generating in background — check History");
      load(filterCfg);
    } catch (e: any) {
      message.error(e.message ?? "Send failed");
    } finally {
      setSendLoading(false);
    }
  }

  const columns: ColumnsType<ReportHistory> = [
    {
      title: "Sent at",
      dataIndex: "sent_at",
      width: 180,
      render: v => new Date(v).toLocaleString(),
      sorter: (a, b) => new Date(b.sent_at).getTime() - new Date(a.sent_at).getTime(),
      defaultSortOrder: "ascend",
    },
    {
      title: "Config",
      dataIndex: "config_name",
      render: v => <Text strong>{v}</Text>,
    },
    {
      title: "Period",
      render: (_, r) => (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {new Date(r.period_start).toLocaleString()} – {new Date(r.period_end).toLocaleString()}
        </Text>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 90,
      render: v => <Tag color={v === "sent" ? "green" : "red"}>{v.toUpperCase()}</Tag>,
    },
    {
      title: "Actions",
      width: 160,
      render: (_, r) => (
        <Space>
          <Button size="small" icon={<EyeOutlined />} onClick={() => setPreview(r)}>
            Preview
          </Button>
          <Button
            size="small"
            icon={<SendOutlined />}
            loading={sending === r.id}
            onClick={() => handleResend(r)}
          >
            Resend
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Flex justify="space-between" align="center" style={{ marginBottom: 16 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>Report History</Typography.Title>
        <Space>
          <Select
            placeholder="Filter by config"
            allowClear
            style={{ width: 200 }}
            options={configs.map(c => ({ value: c.id, label: c.name }))}
            value={filterCfg}
            onChange={v => setFilterCfg(v)}
          />
          <Button icon={<ReloadOutlined />} onClick={() => load(filterCfg)} loading={loading}>
            Refresh
          </Button>
          <Button
            type="primary"
            icon={<SendOutlined />}
            onClick={() => { setSendCfgID(undefined); setSendRange(null); setSendModal(true); }}
          >
            Generate & Send
          </Button>
        </Space>
      </Flex>

      <Table
        rowKey="id"
        dataSource={history}
        columns={columns}
        loading={loading}
        pagination={{ pageSize: 20 }}
        size="middle"
      />

      {/* Preview modal */}
      <Modal
        title={`Report Preview — ${preview?.config_name ?? ""}`}
        open={!!preview}
        onCancel={() => setPreview(null)}
        footer={[
          <Button key="close" onClick={() => setPreview(null)}>Close</Button>,
        ]}
        width={680}
      >
        {preview && (
          <div>
            <Flex justify="space-between" style={{ marginBottom: 8 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Period: {new Date(preview.period_start).toLocaleString()} –{" "}
                {new Date(preview.period_end).toLocaleString()}
              </Text>
              <Tag color={preview.status === "sent" ? "green" : "red"}>
                {preview.status.toUpperCase()}
              </Tag>
            </Flex>
            {preview.error && (
              <Text type="danger" style={{ display: "block", marginBottom: 8 }}>
                Error: {preview.error}
              </Text>
            )}
            <div style={{
              background: "var(--bg-0)",
              border: "1px solid var(--line)",
              borderRadius: 6,
              padding: "12px 16px",
              fontSize: 13,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              maxHeight: 480,
              overflow: "auto",
              lineHeight: 1.7,
              fontFamily: "monospace",
              color: "var(--text)",
            }} dangerouslySetInnerHTML={{ __html: preview.summary || "(no content)" }} />
          </div>
        )}
      </Modal>

      {/* Manual generate & send modal */}
      <Modal
        title="Generate & Send Report"
        open={sendModal}
        onOk={handleManualSend}
        onCancel={() => setSendModal(false)}
        okText="Send"
        confirmLoading={sendLoading}
      >
        <Space direction="vertical" style={{ width: "100%", marginTop: 8 }}>
          <Select
            style={{ width: "100%" }}
            placeholder="Select report config"
            options={configs.map(c => ({ value: c.id, label: c.name }))}
            value={sendCfgID}
            onChange={setSendCfgID}
          />
          <div>
            <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>
              Custom date range (optional — leave blank to use config window)
            </Text>
            <RangePicker
              showTime
              style={{ width: "100%" }}
              value={sendRange ?? undefined}
              onChange={v => setSendRange(v && v[0] && v[1] ? [v[0], v[1]] : null)}
            />
          </div>
        </Space>
      </Modal>
    </div>
  );
}
